import type { Machine } from '../types/machine.types';
import type {
  MaintenanceFormValues,
  MaintenanceStatus,
  MaintenanceSummary,
  MaintenanceTableRow,
  MaintenanceWindow,
  PacsMaintenanceApi,
  ScheduleMaintenancePayload,
} from '../types/maintenance.types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatTime12h(date: Date): string {
  let hours = date.getHours();
  const minutes = pad2(date.getMinutes());
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${pad2(hours)}:${minutes} ${ampm}`;
}

/** e.g. "02 Jul 2026, 04:48 pm" — table cells */
export function formatMaintenanceDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return `${pad2(date.getDate())} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${formatTime12h(date)}`;
}

/** e.g. "Thu, 02 Jul, 2026, 04:48 pm" — detail panel */
export function formatMaintenanceDateTimeLong(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return `${WEEKDAYS[date.getDay()]}, ${pad2(date.getDate())} ${MONTHS[date.getMonth()]}, ${date.getFullYear()}, ${formatTime12h(date)}`;
}

export function getMaintenanceStatusLabel(status: MaintenanceStatus): string {
  switch (status) {
    case 'scheduled':
      return 'Maintenance scheduled';
    case 'in_progress':
      return 'Maintenance in progress';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
}

export function getTimeRemainingLabel(window: MaintenanceWindow, now = new Date()): string | null {
  if (window.status !== 'in_progress') {
    return null;
  }
  const end = new Date(window.scheduledEnd).getTime();
  if (Number.isNaN(end)) {
    return null;
  }
  const diffMs = end - now.getTime();
  if (diffMs <= 0) {
    return 'Ending soon';
  }
  const totalMinutes = Math.round(diffMs / 60000);
  if (totalMinutes < 60) {
    return `${totalMinutes}m left`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m left` : `${hours}h left`;
}

export function deriveMaintenanceStatus(
  startIso: string,
  endIso: string,
  explicit?: MaintenanceStatus,
  now = new Date()
): MaintenanceStatus {
  if (explicit === 'cancelled' || explicit === 'completed') {
    return explicit;
  }
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  const t = now.getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return explicit ?? 'scheduled';
  }
  if (t < start) {
    return 'scheduled';
  }
  if (t >= start && t <= end) {
    return 'in_progress';
  }
  return 'completed';
}

function countWindowTowardSummary(
  w: MaintenanceWindow,
  now: Date,
  weekAhead: Date,
  monthStart: Date,
  counts: {
    activeNow: number;
    upcoming7d: number;
    completedThisMonth: number;
    cancelledAllTime: number;
  }
): void {
  const status = deriveMaintenanceStatus(w.scheduledStart, w.scheduledEnd, w.status, now);
  if (status === 'in_progress') {
    counts.activeNow += 1;
  }
  if (status === 'scheduled') {
    const start = new Date(w.scheduledStart);
    if (start >= now && start <= weekAhead) {
      counts.upcoming7d += 1;
    }
  }
  if (status === 'completed') {
    const end = new Date(w.scheduledEnd);
    if (end >= monthStart) {
      counts.completedThisMonth += 1;
    }
  }
  if (status === 'cancelled') {
    counts.cancelledAllTime += 1;
  }
}

export function computeMaintenanceSummary(
  windows: MaintenanceWindow[],
  now = new Date()
): MaintenanceSummary {
  const weekAhead = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const counts = {
    activeNow: 0,
    upcoming7d: 0,
    completedThisMonth: 0,
    cancelledAllTime: 0,
  };

  for (const w of windows) {
    countWindowTowardSummary(w, now, weekAhead, monthStart, counts);
  }

  return counts;
}

export function mapMaintenanceToTableRow(
  window: MaintenanceWindow,
  now = new Date()
): MaintenanceTableRow {
  const status = deriveMaintenanceStatus(
    window.scheduledStart,
    window.scheduledEnd,
    window.status,
    now
  );
  const subtitleParts = [
    window.modality,
    window.location
      ? `Radiology — ${window.location}`.replace(/^Radiology — Radiology/, 'Radiology')
      : null,
  ].filter(Boolean);

  let machineSubtitle = '';
  if (window.modality && window.location) {
    const loc = window.location.startsWith('Radiology')
      ? window.location
      : `Radiology — ${window.location}`;
    machineSubtitle = `${window.modality} · ${loc}`;
  } else {
    machineSubtitle = subtitleParts.join(' · ');
  }

  return {
    id: window.id,
    machineName: window.machineName,
    machineSubtitle,
    scheduledStart: formatMaintenanceDateTime(window.scheduledStart),
    scheduledEnd: formatMaintenanceDateTime(window.scheduledEnd),
    reason: window.reason || '—',
    status,
    mwlBlocked: window.mwlBlocked && status === 'in_progress',
    timeRemainingLabel: getTimeRemainingLabel({ ...window, status }, now),
  };
}

export function getDefaultMaintenanceFormValues(): MaintenanceFormValues {
  return {
    machineId: '',
    starts: '',
    ends: '',
    reason: '',
  };
}

export function datetimeLocalToIso(value: string): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString();
}

export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  const h = pad2(date.getHours());
  const min = pad2(date.getMinutes());
  return `${y}-${m}-${d}T${h}:${min}`;
}

export function buildSchedulePayload(
  values: MaintenanceFormValues,
  machine: {
    id: number;
    machineName: string;
    modality: string;
    location: string;
  } | null,
  facilityName: string
): ScheduleMaintenancePayload | null {
  if (!machine || !values.starts || !values.ends) {
    return null;
  }
  return {
    machineId: machine.id,
    machineName: machine.machineName,
    modality: machine.modality,
    location: machine.location,
    facilityName,
    scheduledStart: datetimeLocalToIso(values.starts),
    scheduledEnd: datetimeLocalToIso(values.ends),
    reason: values.reason.trim(),
  };
}

function normalizeMaintenanceStatus(raw?: string | null): MaintenanceStatus | undefined {
  if (!raw) {
    return undefined;
  }
  const value = raw.trim().toLowerCase();
  if (value === 'scheduled') {
    return 'scheduled';
  }
  if (value === 'in_progress' || value === 'in progress' || value === 'active') {
    return 'in_progress';
  }
  if (value === 'completed') {
    return 'completed';
  }
  if (value === 'cancelled' || value === 'canceled') {
    return 'cancelled';
  }
  return undefined;
}

export function toPacsMaintenanceBody(payload: ScheduleMaintenancePayload) {
  return {
    startTime: payload.scheduledStart,
    endTime: payload.scheduledEnd,
    reason: payload.reason || '',
  };
}

/** Fill missing modality/location from the machine registry when the API omits them. */
export function enrichMaintenanceWindowsWithMachines(
  windows: MaintenanceWindow[],
  machines: Machine[]
): MaintenanceWindow[] {
  if (!machines.length) {
    return windows;
  }

  const byId = new Map(machines.map(machine => [machine.id, machine]));
  const byName = new Map(machines.map(machine => [machine.machineName.toLowerCase(), machine]));

  return windows.map(window => {
    const registryMachine =
      (window.machineId != null ? byId.get(window.machineId) : undefined) ??
      byName.get(window.machineName.toLowerCase());

    if (!registryMachine) {
      return window;
    }

    return {
      ...window,
      modality: window.modality || registryMachine.modality,
      location: window.location || registryMachine.location,
    };
  });
}

export function mapPacsMaintenanceToWindow(
  item: PacsMaintenanceApi,
  facilityName = ''
): MaintenanceWindow {
  const idSource = item.pk ?? item.id ?? item.maintenancePk;
  const machineId = item.machinePk ?? item.machineId ?? null;
  const scheduledStart = item.startTime ?? item.scheduledStart ?? '';
  const scheduledEnd = item.endTime ?? item.scheduledEnd ?? '';
  const explicitStatus = normalizeMaintenanceStatus(item.status);
  const status = deriveMaintenanceStatus(scheduledStart, scheduledEnd, explicitStatus);

  return {
    id: idSource != null ? String(idSource) : '',
    machineId: machineId != null ? Number(machineId) : null,
    machineName: item.machineName ?? 'Unknown machine',
    modality: item.modality ?? '',
    location: item.location ?? '',
    facilityName,
    scheduledStart,
    scheduledEnd,
    reason: item.reason ?? '',
    status,
    mwlBlocked: item.mwlBlocked ?? (status === 'scheduled' || status === 'in_progress'),
    scheduledBy: item.scheduledBy ?? null,
    createdAt: item.createdAt ?? item.createdTime ?? scheduledStart,
    auditHistory: [],
  };
}
